// Casino Dashboard › All Transactions
//
// Ported from casino_dashboard/src/layouts/alltransactions — the full per-player
// ledger from GET /dashboard/usertransaction, plus the segment CSV exports.
import React from "react";
import { Box, Button, Snackbar, Alert, Typography } from "@mui/material";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import PageHeader from "../../components/PageHeader";
import SectionCard from "../../components/SectionCard";
import DataTable from "../../components/DataTable";
import DashboardService from "../../Utility/dashboardService";
import { tokens } from "../../theme";
import { StateLine, errText, fixed } from "./shared";

// Segment exports: GET /usertransaction/download/{code}/csv
const SEGMENT_EXPORTS = [
  { code: "kycPassed", label: "KYC Passed" },
  { code: "kycFailed", label: "KYC Failed/Pending" },
  { code: "noPackage", label: "Package not bought" },
  { code: "lessBalance", label: "SC balance below 0.5" },
  { code: "dailyLogin", label: "Daily Login Reminder" },
  { code: "N1", label: "N1" },
  { code: "N2", label: "N2" },
  { code: "A1", label: "A1" },
  { code: "A2", label: "A2" },
];

const shortDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("en-US", {
    timeZone: "America/New_York",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const columns = [
  { header: "User Id", accessor: "userid", align: "left" },
  { header: "Name", accessor: "name", align: "left" },
  { header: "State", accessor: "stateName", align: "left" },
  { header: "Signup Date", accessor: "signupDate", align: "left" },
  { header: "Birth Date", accessor: "birthDate", align: "left" },
  { header: "Level", accessor: "vipLavel", align: "left" },
  { header: "Packages", accessor: "pakcages", align: "left" },
  { header: "SC Received", accessor: "sc_received", align: "left" },
  { header: "GC Received", accessor: "gc_received", align: "left" },
  { header: "Signup SC", accessor: "signup_sc", align: "center" },
  { header: "SC WOF", accessor: "daily_login_sc", align: "left" },
  { header: "Refer SC", accessor: "referSC", align: "center" },
  { header: "Transfer SC", accessor: "transferSC", align: "center" },
  { header: "GC to SC", accessor: "gctoSC", align: "center" },
  { header: "Post SC", accessor: "postSC", align: "center" },
  { header: "Mail SC", accessor: "mailSC", align: "center" },
  { header: "SC Redeem", accessor: "sc_redeem", align: "center" },
  { header: "SC Withdrawal", accessor: "sc_widhrawl", align: "center" },
  { header: "SC Play", accessor: "sc_play", align: "center" },
  { header: "SC Win", accessor: "sc_win", align: "center" },
  { header: "Last Update", accessor: "last_update", align: "center" },
];

export default function CasinoAllTransactions() {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [downloading, setDownloading] = React.useState(null);
  const [snack, setSnack] = React.useState(null);

  const fetchData = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    setRows([]);
    try {
      const result = await DashboardService.getData("/dashboard/usertransaction");
      setRows(Array.isArray(result?.data) ? result.data : []);
    } catch (e) {
      setError(errText(e));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  // "Export All" hits a different path shape than the segment exports.
  const download = async (endpoint, filename, key) => {
    setDownloading(key);
    try {
      await DashboardService.downloadFile(endpoint, filename);
      setSnack({ text: `${filename} downloaded.`, sev: "success" });
    } catch (e) {
      setSnack({ text: errText(e), sev: "error" });
    } finally {
      setDownloading(null);
    }
  };

  const tableRows = React.useMemo(
    () =>
      rows.map((t) => ({
        userid: t.id_user,
        name: t.name,
        stateName: t.stateName,
        vipLavel: t.vipLavel,
        birthDate: t.birthDate,
        signupDate: shortDate(t.signupDate),
        pakcages: `$${fixed(t.pakcages)}`,
        sc_received: fixed(t.sc_received),
        gc_received: fixed(t.gc_received),
        signup_sc: fixed(t.signup_sc),
        daily_login_sc: fixed(t.daily_login_sc),
        referSC: fixed(t.refer_bonus_sc),
        transferSC: fixed(t.transfer_sc),
        gctoSC: fixed(t.gc_to_sc),
        postSC: fixed(t.post_sc),
        mailSC: fixed(t.mailing_sc),
        sc_redeem: fixed(t.sc_redeem),
        sc_widhrawl: fixed(t.sc_widhrawl),
        sc_play: fixed(t.sc_play),
        sc_win: fixed(t.sc_win),
        last_update: shortDate(t.last_update),
      })),
    [rows]
  );

  return (
    <Box>
      <PageHeader
        title="All Transactions"
        crumb="Casino Dashboard / All Transactions"
        subtitle="Lifetime coin ledger per player, with segment exports"
        actions={
          <Button
            size="small"
            variant="contained"
            startIcon={<FileDownloadRoundedIcon />}
            onClick={() => download("/usertransaction/download/csv", "transaction.csv", "all")}
            disabled={!!downloading}
          >
            {downloading === "all" ? "Exporting…" : "Export All"}
          </Button>
        }
      />

      <SectionCard title="Segment exports" sx={{ p: 2, mb: 1.75, height: "auto" }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.2 }}>
          Each export downloads the matching player list as CSV.
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
          {SEGMENT_EXPORTS.map(({ code, label }) => (
            <Button
              key={code}
              size="small"
              variant="outlined"
              color="inherit"
              disabled={!!downloading}
              onClick={() =>
                download(`/usertransaction/download/${code}/csv`, `${code}.csv`, code)
              }
              sx={{ fontSize: 12.5, py: 0.4, color: tokens.textDim, borderColor: tokens.border }}
            >
              {downloading === code ? "Exporting…" : label}
            </Button>
          ))}
        </Box>
      </SectionCard>

      <SectionCard title="Transactions" sx={{ p: 2, height: "auto" }}>
        <StateLine error={error} />
        {!error && (
          <DataTable
            columns={columns}
            rows={tableRows}
            loading={loading}
            searchable
            sortable
            paginated
            entriesPerPage
            showTotalEntries
            searchPlaceholder="Search user id, name, state…"
            emptyText="No transactions found."
          />
        )}
      </SectionCard>

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snack?.sev || "success"} onClose={() => setSnack(null)} variant="filled">
          {snack?.text}
        </Alert>
      </Snackbar>
    </Box>
  );
}
